import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from '../../icons';
// «+ 새 기록» → 오른쪽 패널 · KPI 넷 → 분할 건수(2026-10-18 · web 레인 W10-56 · 렌즈 Karri · 시안 56 «고칠 2»)
import { Plus } from '../../icons';
import { SidePanel } from '../../components/shared';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  fetchAwards, fetchUserAwards, createAward, deleteAward,
  AWARD_KIND_GROUPS, AWARD_KIND_LABEL, EXPIRY_GUIDE,
  type AwardDto, type AwardKind, type AwardFilter, type AwardListDto,
} from '../../api/hr-awards';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
import './AwardsPage.css';
// 기준 설정 (2026-08-28 · 판 7) — 종류의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 아래 기본 목록이 그대로 산다(깜빡이지 않는다).
import { useCompanyCodes } from '../../lib/company-codes';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 포상 · 징계 — 담당자 화면 (시안 A20, migration 133).
 *
 * ★★ **포상과 징계가 한 목록**이다. 가르는 것은 종류 배지 하나다 — 표를 나누면 「이 사람의
 *    상벌 이력」 을 볼 때마다 합쳐야 하고, 감경이 어느 쪽에 앉는지 매번 다시 정해야 한다.
 * ★★ **말소 예정을 목록에서 바로 보인다.** 징계는 기간이 지나면 말소되는데, 그 날이 오는 것을
 *    아무도 안 보고 있으면 지난 징계가 승진 심사에 계속 따라다닌다.
 * ★ **말소일·분류·「증명서에 나가나」 는 전부 서버가 준 값이다.** 화면이 「견책은 1년」 을 갖지
 *   않는다 — 취업규칙이 바뀌면 서버 한 곳만 바뀌어야 한다(오른쪽 안내표는 **읽을거리**다).
 * ★ 감경·취소를 고르면 «대상 징계» 칸이 뜨고, **그 사람의 징계만** 후보로 온다.
 */

const FILTERS: ReadonlyArray<{ key: AwardFilter; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'reward', label: '포상' },
  { key: 'punishment', label: '징계' },
  { key: 'relief', label: '감경 · 취소' },
  { key: 'expiring', label: '말소 예정' },
];

interface FormState {
  userId: string;
  kind: AwardKind;
  awardDate: string;
  reason: string;
  basis: string;
  expiresOn: string;
  relatedId: string;
}

const emptyForm = (): FormState => ({
  userId: '', kind: 'COMMENDATION', awardDate: '', reason: '', basis: '', expiresOn: '', relatedId: '',
});

/** 이 종류가 말소되는가 — 후보 목록의 갈래로 안다(값 자체는 서버가 정한다). */
const categoryOfKind = (kind: string) =>
  AWARD_KIND_GROUPS.find((g) => (g.kinds as readonly string[]).includes(kind))?.category ?? null;

export function AwardsPage() {
  // ★ 회사가 고친 이름을 얹는다 — 고를 때와 목록에 그릴 때가 **같은 자리**를 읽어야 한다.
  const codes = useCompanyCodes('hr');
  const [list, setList] = useState<AwardListDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [error, setError] = useState<string | null>(null);
  // 새 기록 패널(W10-56) — 열 때만 그린다. 닫으면 초점은 «새 기록» 단추로 돌아간다(SidePanel).
  const [panel, setPanel] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const [filter, setFilter] = useState<AwardFilter>('all');
  const [year, setYear] = useState<string>('');
  const [search, setSearch] = useState('');

  const [employees, setEmployees] = useState<EmployeeRowDto[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  /** 감경 대상 후보 — 고른 사람의 징계만. 연도 필터와 무관하게 그 사람 전체를 본다. */
  const [reliefTargets, setReliefTargets] = useState<AwardDto[]>([]);

  const load = useCallback(async () => {
    const res = await fetchAwards({ filter, year: year || undefined, search });
    setList(res);
  }, [filter, year, search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [load]);

  useEffect(() => {
    let alive = true;
    (async () => {
      // 고르기 목록은 없어도 화면이 선다 — 따로 삼킨다.
      try {
        const emp = await listEmployees();
        if (alive) setEmployees(emp.rows);
      } catch { /* 사람 고르기만 못 쓴다 */ }
    })();
    return () => { alive = false; };
  }, []);

  const formCategory = categoryOfKind(form.kind);

  // 감경·취소를 고른 뒤 사람을 정하면, 그 사람의 징계를 후보로 가져온다.
  useEffect(() => {
    let alive = true;
    if (formCategory !== 'RELIEF' || !form.userId) { setReliefTargets([]); return; }
    (async () => {
      try {
        const res = await fetchUserAwards(form.userId);
        if (alive) setReliefTargets(res.rows.filter((r) => r.category === 'PUNISHMENT'));
      } catch { if (alive) setReliefTargets([]); }
    })();
    return () => { alive = false; };
  }, [formCategory, form.userId]);

  const years = useMemo(() => list?.years ?? [], [list]);

  const submit = async () => {
    if (!form.userId || !form.awardDate || !form.reason.trim()) {
      setError('대상 · 일자 · 사유는 있어야 합니다');
      return;
    }
    setBusy('new');
    setError(null);
    try {
      await createAward({
        userId: form.userId,
        kind: form.kind,
        awardDate: form.awardDate,
        reason: form.reason.trim(),
        basis: form.basis.trim() || null,
        // ★ 안 보내면 서버가 종류별 기본값을 채운다. 포상에는 아예 보내지 않는다.
        ...(formCategory === 'PUNISHMENT' && form.expiresOn ? { expiresOn: form.expiresOn } : {}),
        ...(formCategory === 'RELIEF' ? { relatedId: form.relatedId || null } : {}),
      });
      setForm(emptyForm());
      await load();
      setPanel(false);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? '등록하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: AwardDto) => {
    setBusy(row.id);
    setError(null);
    try {
      await deleteAward(row.id);
      await load();
    } catch {
      setError('삭제하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const summary = list?.summary;
  const breakdownText = (items: Array<{ label: string; count: number }>) =>
    items.length > 0 ? items.map((b) => `${b.label} ${b.count}`).join(' · ') : '기록 없음';
  // 분할 건수(W10-56) — 전체 = 포상 + 징계 + 감경 · 취소(그 해) · 말소 예정은 전체 해.
  // ★ 한 줄 안에서 셈의 범위가 갈린다(관제 지적 · 판단 순서 ② 잘못 읽음) — 칩 줄 아래에 범위를 글로 적는다.
  //   옛 카드는 «{year}년 포상» · «3개월 안» 으로 이름에 달았다. 말소 예정은 서버가 90일(≈ 3개월) · 모든 해로 센다.
  const countScopeText = year
    ? `전체 · 포상 · 징계 · 감경 · 취소는 ${year}년 기록 · 말소 예정은 앞으로 3개월 안(모든 해)`
    : '전체 · 포상 · 징계 · 감경 · 취소는 모든 해 기록 · 말소 예정은 앞으로 3개월 안';
  const countOf = (key: AwardFilter, sm: NonNullable<typeof summary>) => (
    key === 'all' ? sm.rewards + sm.punishments + sm.reliefs
      : key === 'reward' ? sm.rewards
        : key === 'punishment' ? sm.punishments
          : key === 'relief' ? sm.reliefs
            : sm.expiringSoon
  );

  return (
    <div className="hrpage hrawd">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/records/awards')}
        scope={scopeFor('/erp/hr/records/awards')}
        title="포상 · 징계"
        description="상훈과 징계를 한 목록에 두고, 말소 예정을 미리 보여 줍니다"
        actions={state !== 'folded' ? (
          <Button variant="primary" iconLeft={<Plus size={14} aria-hidden />} onClick={() => { setError(null); setPanel(true); }}>
            새 기록
          </Button>
        ) : undefined}
      />

      {state === 'folded' && (
        <div className="hrpage__note">
          포상 · 징계를 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.
        </div>
      )}

      {state !== 'folded' && (
        <>
          {summary && summary.expiringSoon > 0 && (
            <div className="hrasg__overdue" role="status">
              <AlertCircle size={16} aria-hidden />
              3개월 안에 말소되는 징계가 <b>{summary.expiringSoon}건</b> 있습니다 — 말소된 기록은 더는 불이익에 쓰지 않습니다.
            </div>
          )}
          {error && !panel && <p className="hrcert__err" role="alert">{error}</p>}

          {/*
            KPI 카드 넷은 걷었다(2026-10-18 · W10-56 · 관제 결정 · 사용자 승인 «요약 카드 줄 걷기»).
            그 숫자는 시안 56 «고칠 2»대로 아래 «기록» 의 분할 건수(거르기 칩)와 판 부제로 옮겼다.
            ★ summary 는 고른 «연도» 안에서 센다(검색 · 갈래 거르기와 무관) — 말소 예정 · 효력 있는 징계는 연도와도 무관하게 전체.
              (서버 awards.routes.ts — summarizeAwards(inYear, all))
          */}
          {panel && (
          <SidePanel
            title="새 기록"
            description="말소일은 종류에 따라 서버가 채웁니다 — 포상은 말소하지 않습니다"
            onClose={() => setPanel(false)}
            busy={busy === 'new'}
          >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              등록
              <span className="hrpage__panels">말소일은 종류에 따라 서버가 채웁니다 — 포상은 말소하지 않습니다</span>
            </h2>
            <div className="hrcert__form">
              <label className="hrcert__field">
                <span>종류</span>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as AwardKind, relatedId: '', expiresOn: '' })}
                >
                  {AWARD_KIND_GROUPS.map((g) => (
                    <optgroup key={g.category} label={g.label}>
                      {g.kinds
                        .filter((k) => !codes.isOff('award_kind', k))
                        .map((k) => (
                          <option key={k} value={k}>{codes.labelOf('award_kind', k, AWARD_KIND_LABEL[k]!)}</option>
                        ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label className="hrcert__field">
                <span>대상</span>
                <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value, relatedId: '' })}>
                  <option value="">사람 고르기</option>
                  {employees.map((e) => (
                    <option key={e.userId} value={e.userId}>
                      {e.name} · {e.teamName ?? '회사 직속'} {e.position ?? ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="hrcert__field">
                <span>일자</span>
                <input
                  type="date" value={form.awardDate}
                  onChange={(e) => setForm({ ...form, awardDate: e.target.value })}
                />
              </label>
              <label className="hrcert__field">
                <span>사유</span>
                <input
                  value={form.reason} maxLength={200} placeholder="예) 고객사 장애 대응 공로"
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </label>
              <label className="hrcert__field">
                <span>근거 문서</span>
                <input
                  value={form.basis} maxLength={100} placeholder="예) 인사위원회 의결 2026-14"
                  onChange={(e) => setForm({ ...form, basis: e.target.value })}
                />
              </label>

              {/* 종류가 필요로 하는 칸만 뜬다 — 포상에 말소 기한 칸을 띄우면 규칙이 흐려진다. */}
              {formCategory === 'PUNISHMENT' && (
                <label className="hrcert__field">
                  <span>말소 기한</span>
                  <input
                    type="date" value={form.expiresOn}
                    onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
                  />
                </label>
              )}
              {formCategory === 'REWARD' && (
                <div className="hrcert__field hrawd__static">
                  <span>말소 기한</span>
                  <p>포상은 말소하지 않습니다</p>
                </div>
              )}
              {formCategory === 'RELIEF' && (
                <label className="hrcert__field">
                  <span>대상 징계</span>
                  <select value={form.relatedId} onChange={(e) => setForm({ ...form, relatedId: e.target.value })}>
                    <option value="">
                      {form.userId ? '징계 고르기' : '먼저 대상을 고르세요'}
                    </option>
                    {reliefTargets.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.awardDate} · {codes.labelOf('award_kind', r.kind, r.kindLabel)} · {r.reason}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <Button variant="primary" onClick={submit} disabled={busy === 'new'}>
                {busy === 'new' ? '저장 중…' : '저장'}
              </Button>
            </div>
            {formCategory === 'RELIEF' && (
              <p className="hrasg__note">
                감경 · 취소는 원 징계를 지우지 않습니다 — 새 기록이 서고, 원 징계는 «감경됨» 으로 남습니다.
              </p>
            )}
          </section>
          </SidePanel>
          )}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              기록
              <span className="hrpage__panels">{list?.total ?? 0}건</span>
              {summary && (
                <span className="hrpage__panels">말소 예정 {summary.expiringSoon}건 · 효력 있는 징계 {summary.activePunishments}건(모든 해)</span>
              )}
            </h2>

            <div className="hrawd__bar">
              <div className="hrcert__tabs" role="group" aria-label="거르기">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`hrcert__tab${filter === f.key ? ' is-on' : ''}`}
                    onClick={() => setFilter(f.key)}
                    aria-pressed={filter === f.key}
                    title={summary && f.key === 'reward' ? breakdownText(summary.rewardBreakdown)
                      : summary && f.key === 'punishment' ? breakdownText(summary.punishmentBreakdown) : undefined}
                  >
                    {f.label}
                    {summary && (
                      <span className="hrcert__count">{countOf(f.key, summary)}</span>
                    )}
                  </button>
                ))}
              </div>
              <select
                className="hrawd__year" value={year} aria-label="연도"
                onChange={(e) => setYear(e.target.value)}
              >
                <option value="">전체 연도</option>
                {years.map((y) => <option key={y} value={y}>{y}년</option>)}
              </select>
              <input
                className="hrawd__search" value={search} placeholder="이름 · 사번으로 찾기" aria-label="이름 · 사번으로 찾기"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {summary && <p className="hrawd__scope">{countScopeText}</p>}

            {state === 'loading' ? (
              <p className="hrpage__empty">불러오는 중…</p>
            ) : !list || list.rows.length === 0 ? (
              <p className="hrpage__empty">
                {filter === 'expiring' ? '3개월 안에 말소되는 징계가 없습니다.' : '아직 등록된 기록이 없습니다.'}
              </p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>일자</th>
                    <th>종류</th>
                    <th>대상</th>
                    <th>사유</th>
                    <th>근거</th>
                    <th>말소</th>
                    <th className="hrpage__act">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.id} className={r.expiringSoon ? 'is-overdue' : ''}>
                      <td className="hrawd__nowrap">{r.awardDate}</td>
                      <td>
                        <span className={`hrpage__pill hrawd__kind is-${(r.category ?? 'none').toLowerCase()}`}>
                          {codes.labelOf('award_kind', r.kind, r.kindLabel)}
                        </span>
                      </td>
                      <td>
                        {r.name ?? '—'}
                        {r.teamName && <span className="hrpage__dim"> · {r.teamName}</span>}
                      </td>
                      <td>
                        {r.reason}
                        {r.relievedBy && <span className="hrpage__pill hrawd__relieved">감경됨</span>}
                      </td>
                      <td className="hrpage__dim">{r.basis ?? '—'}</td>
                      <td className="hrawd__nowrap">
                        {r.expiresOn ? (
                          <>
                            {r.expiresOn}
                            {r.expired && <span className="hrpage__pill hrawd__expired">말소됨</span>}
                            {r.expiringSoon && <span className="hrpage__pill hrawd__soon">예정</span>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="hrpage__act">
                        <button
                          type="button" className="hrawd__del"
                          onClick={() => remove(r)} disabled={busy === r.id}
                        >
                          {busy === r.id ? '지우는 중…' : '삭제'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              말소 규칙
              <span className="hrpage__panels">취업규칙 제41조 — 실제 말소일은 등록할 때 정해져 기록에 남습니다</span>
            </h2>
            <table className="hrpage__tbl">
              <thead>
                <tr><th>종류</th><th>말소까지</th><th>증명서 표기</th></tr>
              </thead>
              <tbody>
                {EXPIRY_GUIDE.map((g) => (
                  <tr key={g.label}>
                    <td>{g.label}</td>
                    <td className="hrpage__dim">{g.until}</td>
                    <td className="hrpage__dim">{g.onCertificate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
